import { appClient } from '../../core/api/http';
import { apiUrl } from '../../core/api/api-url';
import type { ContextSummary, PagedContexts, PagedProjects, ProjectSummary } from '../../core/api/types';

/**
 * The practitioner's contexts and projects, read whole so that a row listing actions from more than
 * one container can name and link the two each row belongs to.
 *
 * One statement of the two reads, because two stores need them for the same reason and the clauses
 * that make them correct are easy to copy wrongly: `state: 'all'` on the projects read (the archive
 * shows completed actions under completed projects; search shows actions under hidden and completed
 * projects and in inactive contexts, FR-011), no context filter, and the API's largest page on both.
 * A copy that drops `state: 'all'` leaves exactly the rows those two screens exist to surface as the
 * unlabelled ones — and it still looks right in every test whose fixture has only active projects.
 *
 * **Caching is the caller's, deliberately.** `archive.store.ts` caches the result and `search.store.ts`
 * caches the promise (its searches overlap; the archive's loads do not), and neither is the wrong
 * answer for the other. What is shared is what is asked for, not how often.
 */
export interface ContainerLookups {
  readonly contexts: readonly ContextSummary[];
  readonly projects: readonly ProjectSummary[];
}

/** The API's largest page — both lists are read whole, as every other list read reads its own. */
const CONTAINERS_PAGE_SIZE = 500;

/**
 * Called from an injection context — a store's `withMethods` body — and returns the reader to keep.
 * `apiUrl` injects, so the two URLs have to be resolved here rather than at request time; a version
 * that took only the `HttpClient` and built its URLs inside the returned function threw
 * `NG0203` on every call, and the caller's "a failed lookup is not a failed search" catch swallowed
 * it into three silently unlabelled screens.
 */
/**
 * The contexts half alone, for a caller that needs no projects.
 *
 * `containerLookupsReader` issues TWO whole-account reads in parallel, and the projects one is the
 * expensive half — its list carries a per-project counts aggregate over every action. A screen that
 * consumes only `contexts` pays for that on every visit and throws it away; the settings screen did
 * exactly that until 023's review noticed.
 *
 * Same reason as its sibling, though 034 removed the mechanism: `apiUrl` used to inject, so the URL
 * had to be resolved in an injection context and the returned reader kept, never rebuilt per
 * request (013's NG0203). `apiUrl` is a plain function now, so the hazard is gone — but the readers
 * keep their shape, because callers hold them and the indirection still buys substitutability.
 */
export function contextLookupReader(): () => Promise<readonly ContextSummary[]> {
  return async () => {
    const page = await appClient().get<{ items: ContextSummary[] }>(
      `${apiUrl('/api/v1/contexts')}?page=1&pageSize=${CONTAINERS_PAGE_SIZE}`,
    );
    return page.items;
  };
}

export function containerLookupsReader(): () => Promise<ContainerLookups> {
  return async () => {
    const [contexts, projects] = await Promise.all([
      appClient().get<PagedContexts>(`${apiUrl('/api/v1/contexts')}?pageSize=${CONTAINERS_PAGE_SIZE}`),
      appClient().get<PagedProjects>(`${apiUrl('/api/v1/projects')}?pageSize=${CONTAINERS_PAGE_SIZE}&state=all`),
    ]);
    return { contexts: contexts.items, projects: projects.items };
  };
}

/**
 * The same two lists, taken off a store's already-loaded data rather than read again — 016's row
 * editor derives its two pickers from them (FR-002/FR-003).
 *
 * As **one** value, and that is the whole point of the function: a `{ contexts, projects }` literal
 * written inline would be a new object on every render, so the row's `lookups` prop would change
 * identity constantly and defeat memoisation. Four stores project it, and the two that can be
 * mid-load pass `null` through unchanged. The argument survives the framework change intact — only
 * the words "change detection" become "render".
 */
export function lookupsOf(source: ContainerLookups): ContainerLookups;
export function lookupsOf(source: ContainerLookups | null): ContainerLookups | null;
export function lookupsOf(source: ContainerLookups | null): ContainerLookups | null {
  return source === null ? null : { contexts: source.contexts, projects: source.projects };
}
